<template>
  <div v-if="societe" class="is-marginless is-paddingless section section-details">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ $t('detailSociete.title', {
                libelle: societe.libelle }) }}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button is-cancel btn-modal-annuler" @click="$router.go(-1)">
              {{ $t('detailSociete.buttons.back') }}
            </button>
            <dropdownButton
              :permission="permSocietes.maj"
              :actions="actionSecondary"
              @click="$router.push({
                name: 'm.parametrage.societe/agence',
                params: { id: $route.params.id },
              })"
              type="is-addOrEdit"
              class="mgr-2">
              {{ $t('detailSociete.buttons.edit') }}
            </dropdownButton>
            <a class="button"
               @click="showAddAttachmentModal()">
              <b-icon
                icon="paperclip"
                type="is-default">
              </b-icon>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="columns is-flex pdt-20px">
      <div class="column is-10">
        <user-warning-message :users="warningMessages"/>
        <div class="columns is-multiline">
          <div class="column is-12" id="general">
            <appinfo-general :societe="societe"></appinfo-general>
          </div>
          <div class="column is-12" id="masque">
            <app-info-masque :societe="societe"></app-info-masque>
          </div>
          <div class="column is-12" id="locDefaut">
            <app-info-produit-loc-defaut :societe="societe"></app-info-produit-loc-defaut>
          </div>
          <div class="column is-12" id="associeLocDefaut">
            <app-info-produit-associe-loc-defaut :societe="societe">
            </app-info-produit-associe-loc-defaut>
          </div>
          <div class="column is-12" id="produitAchat">
            <app-info-produit-achat
              :societe="societe"
              :modulePresseActive="modulePresseActive">
            </app-info-produit-achat>
          </div>
          <div class="column is-12" id="produitDivers">
            <info-produit-divers :produits-divers="societe.produitsDivers"></info-produit-divers>
          </div>
          <div class="column is-12" id="produitStock" v-if="moduleStockTiersActive">
            <app-info-produit-stock-par-defaut :produitVente="societe.produitVenteBonStock"
              :produitAchat="societe.produitAchatBonStock">
            </app-info-produit-stock-par-defaut>
          </div>
          <div class="column is-12" id="logistique">
            <info-logistique :societe="societe"></info-logistique>
          </div>
          <div class="column is-12" id="delaiAdministratif">
            <app-info-delai-administratif :societe="societe"></app-info-delai-administratif>
          </div>
          <div class="column is-12" id="signataire">
            <signataire-detail
              :signataire="societe.signataire"
              :emailSignataireRelance="societe.emailSignataireRelance"
              for-societe>
            </signataire-detail>
          </div>
          <div class="column is-6" id="poseOption">
            <app-pose-option :societe="societe"></app-pose-option>
          </div>
          <div class="column is-6" id="fonctionnaliteFacultative">
            <fonctionnalite-facultative :societe="societe"></fonctionnalite-facultative>
          </div>
          <div v-if="isActiveEcheancesAFacturerNegativesAA"
            class="column is-12"
            id="echeancesNegativesVente">
            <info-echeances-negative-vente :societe="societe"></info-echeances-negative-vente>
          </div>
          <div class="column is-12" id="listesAgences">
            <app-liste-agence :societe="societe"></app-liste-agence>
          </div>
          <div class="column is-12" id="infoCompta">
            <app-info-compta :societe="societe"></app-info-compta>
          </div>
          <div v-if="isActiveYield" class="column is-12" id="infoYield">
            <app-info-yield :societe="societe"></app-info-yield>
          </div>
          <div class="column is-6" id="infoClient">
            <app-info-client :societe="societe" detail></app-info-client>
          </div>
          <div class="column is-6" id="infoRegleFacturation">
            <app-info-regle-facturation :societe="societe" detail></app-info-regle-facturation>
          </div>
          <div
            class="column is-12" id="parametresEdition">
            <app-info-parametres-edition
              :societe="societe">
            </app-info-parametres-edition>
          </div>
          <div v-if="moduleIndexationCommande" class="column is-12" id="indexationCommande">
            <gestion-indexation-commande :societe="societe" detail>
            </gestion-indexation-commande>
          </div>
          <div class="column is-12" id="textesEdition">
            <app-info-textes-edition :societe="societe"></app-info-textes-edition>
          </div>
          <div class="column is-12" id="venteInterne" v-if="hasHolding">
            <vente-interne :societe="societe" :readOnly="true"></vente-interne>
          </div>
          <div class="column is-12" id="mentionsLegales">
            <mentions-legales :societe="societe"></mentions-legales>
          </div>
          <div v-if="moduleSignatureElectroniqueActive"
            class="column is-12" id="signatureElectronique">
            <signature-electronique :societe="societe"></signature-electronique>
          </div>
          <div v-if="moduleLogistiqueImpressionActive"
            class="column is-12" id="logistiqueImpression">
            <logistique-impression :societe="societe"></logistique-impression>
          </div>
          <div class="column is-12" id="attachments">
            <div class="card">
              <header class="header-card">
                <p>
                  {{ $t('piecesJointes.attachments')}}
                </p>
              </header>
              <div class="card-content">
                <app-pieces-jointes :checkPermissions="true" autoSave read-only deletable/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-2">
        <dots-menu :steps="steps" class="is-fixed">
        </dots-menu>
      </div>
    </div>
    <b-modal
      v-if="societe"
      v-model="openPopUpFusion"
      :width="800"
      class="modal"
      has-modal-card>
      <pop-up-fusion-societe
        :societe="societe">
      </pop-up-fusion-societe>
    </b-modal>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import { get } from '@/api/societe/index';
import { get as getSchema } from '@/api/schemaComptable/index';
import messageErreur from '@/services/general/messageErreur';
import { gestionEntityNotFound } from '@/services/general/validation/entityNotFound';
import AppBreadcrumb from '@/components/general/breadcrumb';
import AppPiecesJointes from '@/components/general/piecesJointes';
import { SOCIETES } from '@/configuration/general/permissionsParametrageConf';
import DotsMenu from '@/components/general/dotsMenu';
import AppinfoGeneral from '@/components/parametrage/societes/infoGeneral';
import AppListeAgence from '@/components/parametrage/societes/listeAgences';
import AppInfoCompta from '@/components/parametrage/societes/infoCompta';
import AppInfoYield from '@/components/parametrage/societes/infoYield';
import AppInfoClient from '@/components/parametrage/societes/ajoutEditionSociete/fonctionnement/gestionClient';
import AppInfoRegleFacturation from '@/components/parametrage/societes/ajoutEditionSociete/fonctionnement/gestionRegleFacturation';
import AppInfoProduitLocDefaut from '@/components/parametrage/societes/infoProduitLocDefaut';
import AppInfoProduitAssocieLocDefaut from '@/components/parametrage/societes/infoProduitAssocieLocDefaut';
import {
  getLibelleProduitLocDefaut, getProduitLoc, getLibelle,
} from '@/services/societe/getLibelleProduitLocDefaut';
import AppInfoParametresEdition from '@/components/parametrage/societes/infoParametresEdition';
import AppInfoTextesEdition from '@/components/parametrage/societes/infoTextesEdition';
import AppInfoProduitAchat from '@/components/parametrage/societes/infoProduitAchat';
import AppPoseOption from '@/components/parametrage/societes/detailSociete/c_poseOption';
import MentionsLegales from '@/components/parametrage/societes/infoMentionsLegales';
import SignatureElectronique from '@/components/parametrage/societes/infoSignatureElectronique';
import { getAll as getAllProduit, get as getProduit } from '@/api/produit';
import { SENS_ACHAT } from '@/configuration/devisConf';
import {
  getLibelleProduitDiversDefaut,
  getLibelle as getLibelleDivers,
  getProduitDivers,
} from '@/services/societe/getLibelleProduitDiversDefaut';
import infoProduitDivers from '@/components/parametrage/societes/detailSociete/infoProduitDivers';
import fonctionnaliteFacultative from '@/components/parametrage/societes/detailSociete/fonctionaliteFacultative';
import signataireDetail from '@/components/general/signataireDetail';
import AppInfoMasque from '@/components/parametrage/societes/detailSociete/masque';
import AppInfoDelaiAdministratif from '@/components/parametrage/societes/detailSociete/c_delaiAdministratif';
import VenteInterne from '@/components/parametrage/societes/ajoutEditionSociete/m_venteInterne';
import { NO_PAGINATION } from '@/configuration/clientTableConf';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import { siteHasHolding } from '@/services/general/holding';
import { TYPE_HOLDING } from '@/configuration/societe/societe';
import infoLogistique from '@/components/parametrage/societes/detailSociete/infoLogistique';
import AppInfoProduitStockParDefaut from '@/components/parametrage/societes/detailSociete/infoProduitStockParDefaut';
import userWarningMessage from '@/components/commun/user/userWarningMessage';
import gestionIndexationCommande from '@/components/parametrage/societes/ajoutEditionSociete/fonctionnement/gestionIndexationCommande';
import dropdownButton from '@/components/general/button/dropdownButton';
import popUpFusionSociete from '@/components/parametrage/societes/popUpFusionSociete';
import logistiqueImpression from '@/components/parametrage/societes/detailSociete/c_logistiqueImpression';
import infoEcheancesNegativeVente from '@/components/parametrage/societes/detailSociete/infoEcheancesNegativeVente';
import getLibelleProduitImpressionAfficheDefaut from '@/services/societe/getLibelleProduitImpressionAfficheDefaut';

const permissions = createNamespacedHelpers('permissions');
const Attachments = createNamespacedHelpers('attachments');
const Parametres = createNamespacedHelpers('parametres');
const User = createNamespacedHelpers('user');
const Societe = createNamespacedHelpers('societe');

export default {
  data() {
    return {
      permSocietes: SOCIETES,
      societe: null,
      openPopUpFusion: false,
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    ...User.mapGetters({
      isSupport: 'isSupport',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    moduleSignatureElectroniqueActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.signatureElectronique.key);
    },
    steps() {
      return [
        {
          name: this.$i18n.t('detailSociete.steps.generalInfos'),
          ref: '#general',
          active: true,
        },
        {
          name: this.$i18n.t('detailSociete.steps.masque'),
          ref: '#masque',
          active: true,
        },
        {
          name: this.$i18n.t('detailSociete.steps.produitLoc'),
          ref: '#locDefaut',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.produitAssocieLoc'),
          ref: '#associeLocDefaut',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.produitAchat'),
          ref: '#produitAchat',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.produitDivers'),
          ref: '#produitDivers',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.produitStock'),
          ref: '#produitStock',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.logistique'),
          ref: '#logistique',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.delaiAdministratif'),
          ref: '#delaiAdministratif',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.signataire'),
          ref: '#signataire',
          active: false,
        },
        {
          name: this.nameStepPoseOption,
          ref: '#poseOption',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.fonctionnaliteFacultative'),
          ref: '#fonctionnaliteFacultative',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.fonctionnaliteFacultative'),
          ref: '#echeancesNegativesVente',
          active: this.isActiveEcheancesAFacturerNegativesAA,
        },
        {
          name: this.$i18n.t('detailSociete.steps.listeAgences'),
          ref: '#listesAgences',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.infoCompta'),
          ref: '#infoCompta',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.yield'),
          ref: '#infoYield',
          active: this.isActiveYield,
        },
        {
          name: this.$i18n.t('detailSociete.steps.infoClient'),
          ref: '#infoClient',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.infoRegleFacturation'),
          ref: '#infoRegleFacturation',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.parametresEdition'),
          ref: '#parametresEdition',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.indexationCommande'),
          ref: '#indexationCommande',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.textEdition'),
          ref: '#textesEdition',
          active: false,
        },
        {
          name: this.$i18n.t('societes.venteInterne.title'),
          ref: '#venteInterne',
          active: false,
        },
        {
          name: this.$i18n.t('societes.mentionsLegales.title'),
          ref: '#mentionsLegales',
          active: false,
        },
        {
          name: this.$i18n.t('societes.signatureElectronique.title'),
          ref: '#signatureElectronique',
          active: false,
        },
        {
          name: this.$i18n.t('societes.tab.logistiqueImpression'),
          ref: '#logistiqueImpression',
          active: false,
        },
        {
          name: this.$i18n.t('detailSociete.steps.piecesJointes'),
          ref: '#attachments',
          active: false,
        },
      ];
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('listeUtilisateur.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeUtilisateur.parametrage'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$i18n.t('societes.liste'), route: { name: 'l.parametrage.societe/agence' }, isActive: false },
        { title: this.societe.libelle, route: { name: '' }, isActive: true },
      ];
    },
    ...permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    isActiveYield() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.activationYield.key);
    },
    modulePresseActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePresse.key);
    },
    moduleIndexationCommande() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleIndexationCommande.key);
    },
    moduleStockTiersActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleStockTiers.key);
    },
    hasHolding() {
      return siteHasHolding() || this.societe.typeSociete === TYPE_HOLDING;
    },
    interdireModificationBlocageCLGActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.interdireModificationBlocageCLG.key);
    },
    nameStepPoseOption() {
      return this.interdireModificationBlocageCLGActive ? this.$i18n.t('detailSociete.steps.blocage')
        : this.$i18n.t('detailSociete.steps.poseOption');
    },
    warningMessages() {
      return [
        ...this.societe.decideurCommercialIds.map(id => ({
          id, msg: 'utilisateur.warning.utilisateurNameArchive',
        })),
        ...this.societe.utilisateursNonControles.map(id => ({
          id, msg: 'utilisateur.warning.utilisateurNameArchive',
        })),
        ...this.societe.utilisateursDemandeTempoManuelleAPrevenir.map(id => ({
          id, msg: 'utilisateur.warning.utilisateurNameArchive',
        })),
        ...this.societe.utilisateursDemandeTempoManuelle.map(id => ({
          id, msg: 'utilisateur.warning.utilisateurNameArchive',
        })),
        ...this.societe.utilisateursEcheanceNegativeVente.map(id => ({
          id, msg: 'utilisateur.warning.utilisateurNameArchive',
        })),
      ];
    },
    actionSecondary() {
      const actions = [];
      if (this.isSupport) {
        actions.push({
          label: this.$t('detailSociete.buttons.fusion'),
          action: () => { this.openPopUpFusion = true; },
          order: 1,
          disabled: this.societe.id === this.societeActiveId,
          tooltip: this.societe.id === this.societeActiveId ? this.$t('detailSociete.fusionSociete.errors.sameCompanyMergeError') : null,
        });
      }
      return actions;
    },
    moduleLogistiqueImpressionActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key);
    },
    isActiveEcheancesAFacturerNegativesAA() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.echeancesAFacturerNegativesAA.key);
    },
  },
  components: {
    fonctionnaliteFacultative,
    AppBreadcrumb,
    AppPiecesJointes,
    DotsMenu,
    AppinfoGeneral,
    AppListeAgence,
    AppInfoCompta,
    AppInfoProduitLocDefaut,
    AppInfoParametresEdition,
    AppInfoTextesEdition,
    AppInfoProduitAchat,
    AppPoseOption,
    infoProduitDivers,
    signataireDetail,
    AppInfoMasque,
    AppInfoDelaiAdministratif,
    AppInfoYield,
    AppInfoClient,
    AppInfoRegleFacturation,
    MentionsLegales,
    SignatureElectronique,
    VenteInterne,
    AppInfoProduitAssocieLocDefaut,
    infoLogistique,
    userWarningMessage,
    gestionIndexationCommande,
    AppInfoProduitStockParDefaut,
    dropdownButton,
    popUpFusionSociete,
    logistiqueImpression,
    infoEcheancesNegativeVente,
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
      showAddAttachmentModal: 'showAddModal',
    }),
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    async fillProduitAchatTechniqueEtReglementTlpe(societe) {
      const products = (await getAllProduit({
        query: {
          societeId: this.$route.params.id,
          sens: SENS_ACHAT,
          actif: null,
        },
        limit: NO_PAGINATION,
      })).data.result.items;
      const findProduitAchatTechnique = products.find((p) => {
        return p.id === societe.produitAchatTechniqueId;
      });
      const findProduitTlpeReglement = products.find((p) => {
        return p.id === societe.produitReglementTlpeId;
      });
      societe.produitAchat.otTech = findProduitAchatTechnique ? findProduitAchatTechnique.libelle : '';
      societe.produitAchat.produitReglementTlpe = findProduitTlpeReglement ? findProduitTlpeReglement.libelle : '';
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'Societe' });
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.activationYield.key,
        PARAMETRES_INTERNE_KEYS.modulePresse.key,
        PARAMETRES_INTERNE_KEYS.moduleIndexationCommande.key,
        PARAMETRES_INTERNE_KEYS.moduleStockTiers.key,
        PARAMETRES_INTERNE_KEYS.interdireModificationBlocageCLG.key,
        PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key,
        PARAMETRES_INTERNE_KEYS.echeancesAFacturerNegativesAA.key,
      ]);
      const societe = (await get(this.$route.params.id)).data.result;
      const produitsLocDefault = await getLibelleProduitLocDefaut(societe);
      societe.produitLoc = produitsLocDefault.produitsLocation;
      societe.produitsDivers = await getLibelleProduitDiversDefaut(societe);
      societe.produitsAssociesLoc = produitsLocDefault.produitAssocieLocation;
      societe.produitAchat = {};
      if (societe.produitAchatGrattageId !== null
        || societe.produitAchatAffichageId !== null) {
        const AllProduitsAchat = await getProduitDivers(SENS_ACHAT);
        if (societe.produitAchatGrattageId) {
          societe.produitAchat.grattage = getLibelleDivers(
            societe.produitAchatGrattageId,
            AllProduitsAchat,
          );
        }
        if (societe.produitAchatAffichageId) {
          societe.produitAchat.affichage = getLibelleDivers(
            societe.produitAchatAffichageId,
            AllProduitsAchat,
          );
        }
        if (societe.produitAchatCommissionId) {
          societe.produitAchat.achatCommission = getLibelleDivers(
            societe.produitAchatCommissionId,
            AllProduitsAchat,
          );
        }
        if (societe.produitAchatLaisserEnPlaceId) {
          societe.produitAchat.laisserEnPlace = getLibelleDivers(
            societe.produitAchatLaisserEnPlaceId,
            AllProduitsAchat,
          );
        }
      }
      await this.fillProduitAchatTechniqueEtReglementTlpe(societe);
      if (societe.produitIdDefautLoyer) {
        societe.produitAchat.loyer = getLibelle(
          societe.produitIdDefautLoyer,
          await getProduitLoc(SENS_ACHAT),
        );
      }
      if (societe.schemaComptableIdAcompteFacturation) {
        societe.schemaComptableFacturation = (await getSchema(
          societe.schemaComptableIdAcompteFacturation,
        )).data.result;
      }
      if (societe.schemaComptableIdAcompteExtourne) {
        societe.schemaComptableExtourne = (await getSchema(
          societe.schemaComptableIdAcompteExtourne,
        )).data.result;
      }
      if (societe.produitVenteBonStockId) {
        societe.produitVenteBonStock = (await getProduit(societe.produitVenteBonStockId)
        ).data.result;
      }
      if (societe.produitAchatBonStockId) {
        societe.produitAchatBonStock = (await getProduit(societe.produitAchatBonStockId)
        ).data.result;
      }
      societe.produitImpressionAffiche = await getLibelleProduitImpressionAfficheDefaut(societe);
      this.societe = societe;
      await this.setAttachmentElementId({ id: this.$route.params.id });
      this.societe.agences.sort((a, b) => {
        return a.libelle.localeCompare(b.libelle);
      });
      this.setInformationPage(this.$t('pageGeneral.detail', { item: this.societe.libelle }));
      this.steps = this.steps.splice(11, 1);
    } catch (e) {
      if (gestionEntityNotFound(e, { name: 'l.parametrage.societe/agence' })) return;

      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
